import { useEffect, useRef, useCallback, useState } from 'react';
import { debounce } from '../utils/helpers';
import { useNetworkStatus } from './useNetworkStatus';

export interface AutosaveConfig {
  enabled: boolean;
  intervalMs: number; // How often to check for changes and autosave
  debounceMs: number; // How long to wait after last change before saving
  maxRetries: number;
}

export interface AutosaveData {
  id: string | null;
  data: any;
  name: string;
  hasUnsavedChanges: boolean;
}

export interface AutosaveCallbacks {
  saveFunction: (name: string, data: any, existingId?: string) => Promise<string>;
  getDataToSave: () => any;
  getFileName: () => string;
  onSaveSuccess?: (id: string) => void;
  onSaveError?: (error: Error) => void;
  validateBeforeSave?: (validationContext?: any) => { isValid: boolean; message?: string };
}

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'disabled' | 'offline';

export interface UseAutosaveReturn {
  autosaveStatus: AutosaveStatus;
  lastSavedAt: Date | null;
  triggerManualSave: () => Promise<void>;
  markAsChanged: () => void;
  syncWithCurrentData: () => void;
  enableAutosave: (validationContext?: any) => void;
  disableAutosave: () => void;
  suspendAutosave: () => void;
  resumeAutosave: () => void;
  isAutosaveEnabled: boolean;
  retryCount: number;
  lastError: string | null;
  canEnableAutosave: boolean;
}

const DEFAULT_CONFIG: AutosaveConfig = {
  enabled: true,
  intervalMs: 15000,  // 15 seconds - less aggressive since partial saves are primary
  debounceMs: 1000,   // 1 second - wait for user to finish before saving
  maxRetries: 3
};

// TEMPORARY PERFORMANCE DEBUG - Disable expensive operations
const DISABLE_AUTOSAVE_FOR_DEBUG = false;

export const useAutosave = (
  autosaveData: AutosaveData,
  callbacks: AutosaveCallbacks,
  config: Partial<AutosaveConfig> = {}
): UseAutosaveReturn => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('disabled');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(false);
  const [canEnableAutosave, setCanEnableAutosave] = useState(false);
  const [isAutosaveSuspended, setIsAutosaveSuspended] = useState(false);
  
  // Add network status monitoring
  const { status: networkStatus } = useNetworkStatus();
  
  const lastSavedDataRef = useRef<string>('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasChangesRef = useRef(false);
  const isSavingRef = useRef(false);
  const autosaveIdRef = useRef<string | null>(autosaveData.id);

  useEffect(() => {
    autosaveIdRef.current = autosaveData.id;
  }, [autosaveData.id]);
  
  // Monitor network status changes
  useEffect(() => {
    if (!networkStatus.isConnected && autosaveStatus !== 'offline' && autosaveStatus !== 'disabled') {
      setAutosaveStatus('offline');
    } else if (networkStatus.isConnected && autosaveStatus === 'offline') {
      setAutosaveStatus('idle');
      // Clear retry count when coming back online
      setRetryCount(0);
    }
  }, [networkStatus.isConnected, autosaveStatus]);
  
  useEffect(() => {
    if (!isAutosaveEnabled) return;
    
    if (callbacks.validateBeforeSave) {
      const validation = callbacks.validateBeforeSave();
      setCanEnableAutosave(validation.isValid);
      if (!validation.isValid) {
        setIsAutosaveEnabled(false);
        setAutosaveStatus('disabled');
      } else if (autosaveStatus === 'disabled') {
        setAutosaveStatus('idle');
      }
    } else {
      setCanEnableAutosave(true);
    }
  }, [callbacks.validateBeforeSave, isAutosaveEnabled, autosaveStatus]);

  // Core saving logic
  const performSave = useCallback(async ({ forceSave = false } = {}) => {
    // Skip if autosave is suspended (unless forced)
    if (isAutosaveSuspended && !forceSave) {
      console.log('[Autosave] Skipping save - autosave is suspended');
      return;
    }
    
    // Only proceed if autosave is enabled OR it's a forced save
    if (!isAutosaveEnabled && !forceSave) {
      return;
    }
    
    // Check network status - skip automatic saves when offline
    if (!networkStatus.isConnected && !forceSave) {
      console.log('[Autosave] Skipping save - device is offline');
      if (autosaveStatus !== 'offline') {
        setAutosaveStatus('offline');
      }
      return;
    }
    
    // Avoid concurrent saves
    if (isSavingRef.current && !forceSave) {
      return;
    }
    
    // Skip save if no changes detected and not a forced save
    if (!forceSave && !hasChangesRef.current) {
      if (autosaveStatus === 'saving') {
        setAutosaveStatus('idle');
      }
      return;
    }
    
    const currentData = callbacks.getDataToSave();
    const currentDataString = JSON.stringify(currentData);
    const currentIdForSkipCheck = autosaveIdRef.current;
    
    if (!forceSave && currentDataString === lastSavedDataRef.current && currentIdForSkipCheck) {
      hasChangesRef.current = false;
      if (autosaveStatus === 'saving') {
        setAutosaveStatus('idle');
      }
      return;
    }
    
    isSavingRef.current = true;
    setAutosaveStatus('saving');
    setLastError(null);
    
    try {
      const fileName = callbacks.getFileName();
      const idToUseForSave = autosaveIdRef.current;
      const savedId = await callbacks.saveFunction(fileName, currentData, idToUseForSave || undefined);
      
      lastSavedDataRef.current = currentDataString;
      hasChangesRef.current = false; // Reset changes flag after successful save
      setAutosaveStatus('saved');
      setLastSavedAt(new Date());
      setRetryCount(0);
      
      // 🔧 CRITICAL FIX: Update the ref immediately to prevent duplicate file creation
      // Without this, rapid successive saves can use stale null ID before state propagates
      autosaveIdRef.current = savedId;
      
      if (callbacks.onSaveSuccess) {
        callbacks.onSaveSuccess(savedId);
      }
      
      setTimeout(() => {
        // Only revert to 'idle' if still enabled and not 'disabled' (e.g. validation failed in meantime)
        if (isAutosaveEnabled && autosaveStatus !== 'disabled') {
          setAutosaveStatus('idle');
        } else if (!isAutosaveEnabled && autosaveStatus === 'saved') { // For manual save when disabled
          setAutosaveStatus('disabled'); // Go back to disabled if it was a manual save while disabled
        }
      }, 800); // Reduced from 2000ms to 800ms for faster feedback
      
    } catch (error) {
      console.error('[Autosave] Save failed:', error);
      const errorMessage = (error as Error).message;
      const errorCode = (error as any)?.code;
      setLastError(errorMessage);
      
      // Check if this is a timeout error (don't retry these)
      const isTimeoutError = errorCode === '57014' || errorMessage.includes('statement timeout');
      
      if (isTimeoutError) {
        console.error('[Autosave] Non-retriable timeout error detected:', {
          errorCode,
          errorMessage
        });
        setAutosaveStatus('error');
        if (callbacks.onSaveError) {
          callbacks.onSaveError(error as Error);
        }
      }
      // Don't retry if we're offline - just set offline status
      else if (!networkStatus.isConnected) {
        console.log('[Autosave] Save failed due to offline status');
        setAutosaveStatus('offline');
      } else if (!forceSave && retryCount < finalConfig.maxRetries) {
        console.log(`[Autosave] Retrying save (attempt ${retryCount + 1}/${finalConfig.maxRetries})`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          if (hasChangesRef.current && isAutosaveEnabled) {
            performSave(); // Call performSave directly for retry
          }
        }, Math.pow(2, retryCount) * 1000);
      } else {
        setAutosaveStatus('error');
        if (callbacks.onSaveError) {
          callbacks.onSaveError(error as Error);
        }
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [
      isAutosaveEnabled, // Added
      networkStatus.isConnected, // Added for network status checks
      callbacks.validateBeforeSave, 
      callbacks.getDataToSave, 
      callbacks.getFileName, 
      callbacks.saveFunction,
      callbacks.onSaveSuccess, 
      callbacks.onSaveError,
      autosaveStatus, // Added as it's used in setTimeout
      finalConfig.maxRetries, // Added for retry logic
      retryCount // Added for retry logic
      // autosaveIdRef is a ref, not needed in deps here
      // lastSavedDataRef is a ref
      // isSavingRef is a ref
      // hasChangesRef is a ref
  ]);

  const debouncedSave = useCallback(
    debounce(() => {
      // Only call performSave if autosave is enabled. 
      // Manual saves will call performSave directly with forceSave.
      if (isAutosaveEnabled) {
        performSave();
      }
    }, finalConfig.debounceMs),
    [performSave, finalConfig.debounceMs, isAutosaveEnabled] // performSave is now a dependency
  );
  
  const markAsChanged = useCallback(() => {
    // TEMPORARY: Skip autosave operations
    if (DISABLE_AUTOSAVE_FOR_DEBUG) {
      console.log('🚫 DEBUG: Autosave disabled');
      return;
    }
    
    if (!isAutosaveEnabled) {
        // If not enabled, don't set hasChangesRef or call debouncedSave,
        // but if the status was 'saved' (e.g., after a manual save), allow it to clear to 'idle' (or 'disabled')
        // This might happen if autosave is disabled, user manual saves, then makes a change.
  
        if (autosaveStatus === 'saved' || autosaveStatus === 'error') {
             setAutosaveStatus(isAutosaveEnabled ? 'idle' : 'disabled');
        }
        return;
    }
    
    hasChangesRef.current = true;
    if (autosaveStatus === 'saved' || autosaveStatus === 'error') { // Also clear error on new change
      setAutosaveStatus('idle');
    }
    debouncedSave();
  }, [debouncedSave, isAutosaveEnabled, autosaveStatus]);
  
  const triggerManualSave = useCallback(async () => {
    if (isSavingRef.current) {
      return;
    }
    // Manually set hasChangesRef to true to ensure save logic runs,
    // especially if no automatic changes were detected yet.
    hasChangesRef.current = true; 
    return performSave({ forceSave: true });
  }, [performSave]); // performSave is now a dependency
  
  const enableAutosave = useCallback((validationContext?: any) => {
    console.log('[Autosave] enableAutosave called', { validationContext });
    
    // Check current validation status, using context if provided
    const validationCheck = () => {
      if (callbacks.validateBeforeSave) {
        return callbacks.validateBeforeSave(validationContext);
      }
      return { isValid: true };
    };
    const validationResult = validationCheck();
      
    if (validationResult.isValid) {
      setIsAutosaveEnabled(true);
      setAutosaveStatus('idle');
      
      // IMPORTANT: Initialize lastSavedDataRef with current data to prevent immediate save on enable
      // When loading existing data, we don't want to immediately trigger a save
      const currentData = callbacks.getDataToSave();
      const currentDataString = JSON.stringify(currentData);
      const dataLength = currentDataString.length;
      lastSavedDataRef.current = currentDataString;
      
      // CRITICAL: Reset hasChangesRef to false to prevent interval-based autosave from triggering
      // Without this, the 5-second interval check will see hasChangesRef=true and trigger a save
      hasChangesRef.current = false;
      
      console.log('[Autosave] ✅ Enabled - initialized refs to prevent redundant save on load', {
        dataLength,
        hasData: dataLength > 0,
        hasChangesReset: true
      });
    } else {
      console.warn('[Autosave] ❌ Cannot enable autosave: validation failed -', validationResult.message);
      setAutosaveStatus('disabled');
    }
  }, [callbacks]);

  const disableAutosave = useCallback(() => {
    setIsAutosaveEnabled(false);
    setAutosaveStatus('disabled');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const suspendAutosave = useCallback(() => {
    console.log('[Autosave] Suspending autosave for batch operation');
    setIsAutosaveSuspended(true);
  }, []);

  const resumeAutosave = useCallback(() => {
    console.log('[Autosave] Resuming autosave after batch operation');
    setIsAutosaveSuspended(false);
    
    // Trigger a save if there are pending changes
    if (hasChangesRef.current && isAutosaveEnabled) {
      console.log('[Autosave] Triggering save after resume due to pending changes');
      // Small delay to ensure state has settled
      setTimeout(() => {
        performSave();
      }, 100);
    }
  }, [isAutosaveEnabled, performSave]);

  // Sync current data as saved (used after partial updates to avoid redundant full saves)
  const syncWithCurrentData = useCallback(() => {
    const currentData = callbacks.getDataToSave();
    const currentDataString = JSON.stringify(currentData);
    lastSavedDataRef.current = currentDataString;
    hasChangesRef.current = false;
    
    console.log('[Autosave] 🔄 Synced with current data (partial save completed):', {
      dataLength: currentDataString.length,
      hasChanges: false
    });
  }, [callbacks]);

  // Set up interval for periodic checks (only if intervalMs > 0)
  useEffect(() => {
    if (!isAutosaveEnabled || !finalConfig.intervalMs) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    intervalRef.current = setInterval(() => {
      if (hasChangesRef.current && !isSavingRef.current) {
        debouncedSave();
      }
    }, finalConfig.intervalMs);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutosaveEnabled, finalConfig.intervalMs, debouncedSave]);
  
  // Mark as changed when the data dependency changes
  // IMPORTANT: Only trigger if the data is DIFFERENT from what was last saved
  // Don't trigger on initial load or when data matches last save
  useEffect(() => {
    if (autosaveData.hasUnsavedChanges && isAutosaveEnabled) {
      // Check if current data is actually different from last saved
      const currentData = callbacks.getDataToSave();
      const currentDataString = JSON.stringify(currentData);
      
      // Only mark as changed if data is truly different from last save
      if (currentDataString !== lastSavedDataRef.current) {
        markAsChanged();
      }
      // If data matches, hasUnsavedChanges flag is stale - will be cleared by parent component
    }
  }, [autosaveData.hasUnsavedChanges, markAsChanged, isAutosaveEnabled, callbacks]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  return {
    autosaveStatus,
    lastSavedAt,
    triggerManualSave,
    markAsChanged,
    syncWithCurrentData,
    enableAutosave,
    disableAutosave,
    suspendAutosave,
    resumeAutosave,
    isAutosaveEnabled,
    retryCount,
    lastError,
    canEnableAutosave
  };
}; 